import React, { useState, useEffect, useMemo, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { BOOKMAKER_ALIASES } from '../utils/constants';
import { useBooks } from '../contexts/BooksContext';
import AddToScratchpadLink from './AddToScratchpadLink';
import ModalBrandingBar from './ModalBrandingBar';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// NBA season crosses calendar years (e.g. 2026-27), so the current season
// year is the calendar year the season started in. Roll over on Aug 1 so
// preseason (late Sep/early Oct) already reflects the upcoming season
// instead of last year's finished one.
const getDefaultNbaStatsYear = () => {
  const now = new Date();
  const y = now.getFullYear();
  return String(now.getMonth() >= 7 ? y : y - 1);
};

// Module-level cache of preloaded HTMLImageElement by logo URL so each render
// doesn't re-create Image objects and the on-canvas logo plugin can read a
// complete img synchronously once the browser has decoded it.
const nbaLogoImageCache = new Map();
const getNbaLogoImage = (url) => {
  if (!url) return null;
  if (nbaLogoImageCache.has(url)) return nbaLogoImageCache.get(url);
  const img = new window.Image();
  img.src = url;
  nbaLogoImageCache.set(url, img);
  return img;
};

const NBAPlayerModal = ({
  show,
  onHide,
  playerName,
  playerTeam,
  playerPosition,
  playerId,
  gameId,
  game,
  getTeamLogoUrl,
  getBookLogoUrl,
  onOddsClick,
  onRemoveBet,
  betslipItems = [],
  betslipCount = 0,
  bookmakers = [],
  initialMarket = 'points',
  recalcEdgePlayer = false,
  onToggleScratchpad,
  isInScratchpad,
}) => {
  const { isBookEnabledForGraphs } = useBooks();
  // Helper to get the actual data key for a bookmaker
  // Note: API now returns data with display names (e.g., "caesars" not "williamhill_us")
  // so we just return the book name directly
  const getBookmakerDataKey = (displayBook) => {
    return displayBook;
  };

  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMarketType, setSelectedMarketType] = useState(initialMarket);
  const [showOver, setShowOver] = useState(true);
  const [showUnder, setShowUnder] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [modalSize, setModalSize] = useState(window.innerWidth >= 1200 ? 'xl' : 'lg');

  // Chart state
  const [showChart, setShowChart] = useState(false);
  const [chartMarket, setChartMarket] = useState(null);
  const [chartPoint, setChartPoint] = useState(null);
  const [chartIsOver, setChartIsOver] = useState(true); // Track if showing over or under
  const [oddsHistoryData, setOddsHistoryData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  // Tooltip state for edge %
  const [tooltipData, setTooltipData] = useState(null);

  // Stats state
  const [showStats, setShowStats] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showDNPs, setShowDNPs] = useState(false);
  const [showMinutes, setShowMinutes] = useState(false);
  const [showFirstPoints, setShowFirstPoints] = useState(false);
  const [showFirstTeamPoints, setShowFirstTeamPoints] = useState(false);
  const [showVsOpponent, setShowVsOpponent] = useState(false);
  const [statsYear, setStatsYear] = useState(() => getDefaultNbaStatsYear());
  // Bumped when a preloaded opponent logo finishes loading so React
  // re-renders the Bar component, which triggers Chart.js to redraw and the
  // teamLogoPlugin to pick up the now-decoded image.
  const [logoLoadTick, setLogoLoadTick] = useState(0);

  // Betslip feedback state
  const [showAddedToSlip, setShowAddedToSlip] = useState(false);
  const prevBetslipCountRef = useRef(betslipCount);

  useEffect(() => {
    if (betslipCount > prevBetslipCountRef.current) {
      setShowAddedToSlip(true);
      setTimeout(() => setShowAddedToSlip(false), 1500);
    }
    prevBetslipCountRef.current = betslipCount;
  }, [betslipCount]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setModalSize(window.innerWidth >= 1200 ? 'xl' : 'lg');
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch player data when modal opens
  useEffect(() => {
    if (show && playerId && gameId) {
      setSelectedMarketType(initialMarket); // Set to the market they clicked from
      fetchPlayerData();
    } else if (!show) {
      // Reset state when modal closes
      setPlayerData(null);
      setError(null);
      setShowOver(true);
      setShowUnder(false);
      setShowChart(false);
      setChartMarket(null);
      setChartPoint(null);
      setChartIsOver(true);
      setOddsHistoryData(null);
      setSelectedTier(null);
      setShowStats(false);
      setStatsData(null);
      setShowDNPs(false);
      setShowMinutes(false);
      setShowFirstPoints(false);
      setShowFirstTeamPoints(false);
      setShowVsOpponent(false);
      setStatsYear(getDefaultNbaStatsYear());
    }
  }, [show, playerId, gameId, initialMarket]);

  // Fetch stats whenever the modal is open or the Current/Last toggle flips.
  // Null out stale data up front so the Bar chart unmounts rather than
  // reconciling against a game log from the other season.
  useEffect(() => {
    if (!show || !playerId) return;
    setStatsData(null);
    fetchPlayerStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, playerId, statsYear]);

  // Fetch odds history for a specific market and tier
  const fetchOddsHistory = async (marketKey, point = null, isOver = true) => {
    if (!gameId || !playerId || !marketKey) {
      console.error('Missing required parameters for odds history');
      return;
    }

    setLoadingChart(true);
    setError(null);
    try {
      const response = await fetch(`/api/games/nba/${gameId}/${playerId}/${marketKey}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setOddsHistoryData(data);
      setSelectedTier(point);
      setShowChart(true);
      setChartMarket(marketKey);
      setChartPoint(point);
      setChartIsOver(isOver); // Track whether we're showing over or under
    } catch (err) {
      console.error('Error fetching odds history:', err);
      setError(err.message);
    } finally {
      setLoadingChart(false);
    }
  };

  const handleBackToTable = () => {
    setShowChart(false);
    setChartMarket(null);
    setChartPoint(null);
    setChartIsOver(true);
    setOddsHistoryData(null);
    setSelectedTier(null);
  };

  // Fetch player stats
  const fetchPlayerStats = async () => {
    if (!playerId) {
      console.error('Missing playerId for stats fetch');
      return;
    }

    setLoadingStats(true);
    try {
      const response = await fetch(`/api/stats/nba/${statsYear}/${playerId}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('Player stats:', data);
      setStatsData(data);
    } catch (err) {
      console.error('Error fetching player stats:', err);
      setStatsData(null);
    } finally {
      setLoadingStats(false);
    }
  };

  // Handle stats toggle
  const handleStatsToggle = () => {
    const newShowStats = !showStats;
    setShowStats(newShowStats);
    if (newShowStats && !statsData && !loadingStats) {
      fetchPlayerStats();
    }
  };

  // Map market types to stat fields
  const getStatFieldForMarket = (marketKey) => {
    const marketToStat = {
      'player_points': 'pts',
      'player_assists': 'ast',
      'player_rebounds': 'reb',
      'player_steals': 'stl',
      'player_blocks': 'blk',
      'player_turnovers': 'turnover',
      'player_threes': 'fg3m',
      'player_first_basket': 'first_fg',
      'player_first_team_basket': 'first_team_fg',
      'points': 'pts',
      'assists': 'ast',
      'rebounds': 'reb',
      'steals': 'stl',
      'blocks': 'blk',
      'turnovers': 'turnover',
      'threes': 'fg3m',
      'first_basket': 'first_fg',
      'first_team_basket': 'first_team_fg',
    };
    return marketToStat[marketKey] || null;
  };

  // Get combo stat value for combined markets
  const getComboStatValue = (game, marketKey) => {
    if (marketKey === 'player_points_rebounds_assists' || marketKey === 'points_rebounds_assists' || marketKey === 'pra') {
      return (game.pts || 0) + (game.reb || 0) + (game.ast || 0);
    }
    if (marketKey === 'player_points_rebounds' || marketKey === 'points_rebounds') {
      return (game.pts || 0) + (game.reb || 0);
    }
    if (marketKey === 'player_points_assists' || marketKey === 'points_assists') {
      return (game.pts || 0) + (game.ast || 0);
    }
    if (marketKey === 'player_rebounds_assists' || marketKey === 'rebounds_assists') {
      return (game.reb || 0) + (game.ast || 0);
    }
    return null;
  };

  // Count how many stats are 10+ for double/triple double
  const countStats10Plus = (game) => {
    let count = 0;
    if ((game.pts || 0) >= 10) count++;
    if ((game.reb || 0) >= 10) count++;
    if ((game.ast || 0) >= 10) count++;
    if ((game.stl || 0) >= 10) count++;
    if ((game.blk || 0) >= 10) count++;
    return count;
  };

  // Check if game qualifies for double double (2+ stats at 10+)
  const isDoubleDouble = (game) => countStats10Plus(game) >= 2 ? 1 : 0;

  // Check if game qualifies for triple double (3+ stats at 10+)
  const isTripleDouble = (game) => countStats10Plus(game) >= 3 ? 1 : 0;

  // Calculate opponent team abbreviation from today's game
  const opponentTeam = useMemo(() => {
    if (!game || !playerTeam) return null;
    return playerTeam === game.away ? game.home : game.away;
  }, [game, playerTeam]);

  // Prepare stats bar chart data
  const prepareStatsChartData = useMemo(() => {
    if (!statsData || !statsData.gameLog || statsData.gameLog.length === 0) {
      return null;
    }

    const statField = getStatFieldForMarket(selectedMarketType);
    const isComboMarket = selectedMarketType === 'pra' ||
      (selectedMarketType.includes('_') &&
        (selectedMarketType.includes('points_rebounds') ||
         selectedMarketType.includes('points_assists') ||
         selectedMarketType.includes('rebounds_assists')));
    const isDoubleDoubleMarket = selectedMarketType === 'double_double';
    const isTripleDoubleMarket = selectedMarketType === 'triple_double';
    const isFirstBasketMarket = selectedMarketType === 'first_basket' || selectedMarketType === 'player_first_basket';
    const isFirstTeamBasketMarket = selectedMarketType === 'first_team_basket' || selectedMarketType === 'player_first_team_basket';

    if (!statField && !isComboMarket && !isDoubleDoubleMarket && !isTripleDoubleMarket && !isFirstBasketMarket && !isFirstTeamBasketMarket) {
      return null;
    }

    // Filter games based on showDNPs and showVsOpponent settings, then sort by date (most recent first)
    const sortedGames = [...statsData.gameLog]
      .filter(game => {
        const minutes = typeof game.min === 'string' ? parseInt(game.min) : game.min;
        // If showDNPs is true, include all games; otherwise filter out 0 minute games
        if (!showDNPs && minutes === 0) return false;
        // If showVsOpponent is true, only include games against today's opponent
        if (showVsOpponent && opponentTeam) {
          const gameOpponent = game.awayTeamAbbrev === playerTeam ? game.homeTeamAbbrev : game.awayTeamAbbrev;
          // Also check homeTeamAbbrev in case player's team is the home team
          const isVsOpponent = game.awayTeamAbbrev === opponentTeam || game.homeTeamAbbrev === opponentTeam;
          if (!isVsOpponent) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.gameDate) - new Date(a.gameDate));

    // Calculate custom averages excluding DNP games (0 minutes)
    const playedGames = statsData.gameLog.filter(game => {
      const minutes = typeof game.min === 'string' ? parseInt(game.min) : game.min;
      return minutes > 0;
    });
    const gamesPlayedCount = playedGames.length;

    // Date-only labels on the x-axis; the opponent (@/vs text + team logo)
    // is drawn on-canvas by teamLogoPlugin, aligned to each bar's x center.
    // Convert to Eastern timezone for consistent display.
    const labels = sortedGames.map(game => {
      const date = new Date(game.gameDate + 'T12:00:00-05:00');
      return `${date.getMonth() + 1}/${date.getDate()}`;
    });

    // Mirrored under the chart as the @/vs text + team logo row.
    const opponents = sortedGames.map(game => {
      const isHome = game.teamId === game.homeTeamId;
      return {
        abbrev: isHome ? game.awayTeamAbbrev : game.homeTeamAbbrev,
        isHome,
      };
    });

    // Score differential (player team final - opponent final) per game.
    // Null when scores are missing — plugin skips nulls so no row is drawn.
    const scoreDiffs = sortedGames.map((game, idx) => {
      const h = Number(game.homeTeamScore ?? game.homeScore);
      const a = Number(game.awayTeamScore ?? game.awayScore);
      if (!Number.isFinite(h) || !Number.isFinite(a)) return null;
      const isHome = opponents[idx]?.isHome;
      if (isHome == null) return null;
      return isHome ? h - a : a - h;
    });

    // Minute-by-minute on-court averages across the filtered games.
    // Each timeline char is `tickSeconds` (usually 10) wide, so a minute is
    // 6 ticks. For each minute 1..48 we tally seconds on court per game and
    // average across games that contributed a timeline for that minute.
    const MINUTE_COUNT = 48;
    const minuteSecTotals = new Array(MINUTE_COUNT).fill(0);
    const minuteGameCounts = new Array(MINUTE_COUNT).fill(0);
    sortedGames.forEach(game => {
      const timeline = game.onCourt && typeof game.onCourt.timeline === 'string'
        ? game.onCourt.timeline
        : null;
      if (!timeline) return;
      const tickSec = (game.onCourt && game.onCourt.tickSeconds) || 10;
      const ticksPerMinute = Math.max(1, Math.round(60 / tickSec));
      for (let m = 0; m < MINUTE_COUNT; m++) {
        const start = m * ticksPerMinute;
        if (start >= timeline.length) break;
        const end = Math.min(start + ticksPerMinute, timeline.length);
        let ones = 0;
        for (let i = start; i < end; i++) {
          if (timeline[i] === '1') ones++;
        }
        minuteSecTotals[m] += ones * tickSec;
        minuteGameCounts[m] += 1;
      }
    });
    const minuteAvgSeconds = minuteSecTotals.map(
      (total, i) => (minuteGameCounts[i] > 0 ? total / minuteGameCounts[i] : 0)
    );
    const hasOnCourtData = minuteGameCounts.some(c => c > 0);

    const data = sortedGames.map(game => {
      const minutes = typeof game.min === 'string' ? parseInt(game.min) : game.min;
      if (isDoubleDoubleMarket) {
        // DNP games show no bar for DD/TD
        if (minutes === 0) return null;
        return isDoubleDouble(game);
      }
      if (isTripleDoubleMarket) {
        // DNP games show no bar for DD/TD
        if (minutes === 0) return null;
        return isTripleDouble(game);
      }
      if (isFirstBasketMarket) {
        // DNP games show no bar
        if (minutes === 0) return null;
        // first_fg is boolean: true = scored first basket, false = did not
        return game.first_fg ? 1 : 0;
      }
      if (isFirstTeamBasketMarket) {
        // DNP games show no bar
        if (minutes === 0) return null;
        // first_team_fg is boolean: true = team scored first basket, false = did not
        return game.first_team_fg ? 1 : 0;
      }
      if (isComboMarket) {
        const val = getComboStatValue(game, selectedMarketType);
        // Show small bar for 0 stats when player actually played
        if (val === 0 && minutes > 0) return 0.4;
        return val;
      }
      const val = game[statField] || 0;
      // Show small bar for 0 stats when player actually played
      if (val === 0 && minutes > 0) return 0.4;
      return val;
    });

    // Get the current line point value for the reference line
    // Find the line with the most isMain: true flags across books
    // Map short names to full market keys
    const marketKeyMap = {
      'pra': 'player_points_rebounds_assists',
      'points_rebounds': 'player_points_rebounds',
      'points_assists': 'player_points_assists',
      'rebounds_assists': 'player_rebounds_assists'
    };
    const lookupKey = marketKeyMap[selectedMarketType] || `player_${selectedMarketType}`;
    const currentMarket = playerData?.markets?.find(m => m.marketKey === lookupKey || m.marketKey === selectedMarketType);
    let lineValue = null;
    if (currentMarket?.lines) {
      let maxMainCount = 0;
      currentMarket.lines.forEach(line => {
        if (line.isSpecial) return;
        // Count how many books have isMain: true for this line
        let mainCount = 0;
        if (line.books) {
          Object.values(line.books).forEach(bookData => {
            // Check for isMain at various levels
            if (bookData.isMain === true || bookData.over_isMain === true || bookData.under_isMain === true || bookData.yes_isMain === true) {
              mainCount++;
            }
          });
        }
        if (mainCount > maxMainCount) {
          maxMainCount = mainCount;
          lineValue = line.point;
        }
      });
      // Fallback: if no isMain found, use the line with the most books offering it
      if (lineValue === null) {
        let maxBookCount = 0;
        currentMarket.lines.forEach(line => {
          if (line.isSpecial) return;
          const bookCount = line.books ? Object.keys(line.books).length : 0;
          if (bookCount > maxBookCount) {
            maxBookCount = bookCount;
            lineValue = line.point;
          }
        });
      }
      // Final fallback to first non-special line
      if (lineValue === null) {
        const firstLine = currentMarket.lines.find(l => !l.isSpecial);
        lineValue = firstLine?.point;
      }
    }

    // For binary markets (DD/TD), color based on yes/no and make Yes twice as tall
    const isBinaryMarket = isDoubleDoubleMarket || isTripleDoubleMarket || isFirstBasketMarket || isFirstTeamBasketMarket;

    // For binary markets, transform data for chart display
    const chartData = isBinaryMarket ? data.map(val => {
      if (val === null) return null; // DNP - no bar
      // All NBA binary markets (first basket, DD, TD): Yes = 2 (green),
      // No = 0.25 (small red bar). Keeps No bars visually subordinate so
      // the eye lands on the Yes/hit rate.
      return val === 1 ? 2 : 0.25;
    }) : data;

    const datasets = [];

    // For first_basket market, add first_points overlay when toggle is on
    if (isFirstBasketMarket && showFirstPoints) {
      const firstPointsData = sortedGames.map(game => {
        const minutes = typeof game.min === 'string' ? parseInt(game.min) : game.min;
        if (minutes === 0) return null; // DNP
        // Same height as first_basket: 2 when has first points, 0.25 when none
        const fp = game.first_points || 0;
        return fp > 0 ? 2 : 0.25;
      });
      datasets.push({
        type: 'bar',
        label: '1st Points',
        data: firstPointsData,
        backgroundColor: 'rgba(148, 163, 184, 0.5)',
        borderColor: 'rgba(148, 163, 184, 0.7)',
        borderWidth: 1,
        barThickness: 30,
        order: 3 // Render behind main bars
      });
    }

    // For first_team_basket market, add first_team_points overlay when toggle is on
    if (isFirstTeamBasketMarket && showFirstTeamPoints) {
      const firstTeamPointsData = sortedGames.map(game => {
        const minutes = typeof game.min === 'string' ? parseInt(game.min) : game.min;
        if (minutes === 0) return null; // DNP
        // Same height as first_team_basket: 2 when has first team points, 0.25 when none
        const ftp = game.first_team_points || 0;
        return ftp > 0 ? 2 : 0.25;
      });
      datasets.push({
        type: 'bar',
        label: '1st Team Points',
        data: firstTeamPointsData,
        backgroundColor: 'rgba(148, 163, 184, 0.5)',
        borderColor: 'rgba(148, 163, 184, 0.7)',
        borderWidth: 1,
        barThickness: 30,
        order: 3 // Render behind main bars
      });
    }

    // Main stat bars
    datasets.push({
      type: 'bar',
      label: selectedMarketType.replace('player_', '').replace(/_/g, ' ').toUpperCase(),
      data: chartData,
      backgroundColor: data.map(val => {
        if (isBinaryMarket) {
          return val === 1 ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
        }
        return lineValue && val >= lineValue ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)';
      }),
      borderColor: data.map(val => {
        if (isBinaryMarket) {
          return val === 1 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)';
        }
        return lineValue && val >= lineValue ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)';
      }),
      borderWidth: 1,
      barThickness: 30,
      order: 2
    });

    // Add horizontal line at the betting line value (not for binary markets)
    if (lineValue !== null && !isBinaryMarket) {
      datasets.push({
        type: 'line',
        label: `Line (${lineValue})`,
        data: Array(data.length).fill(lineValue),
        borderColor: 'rgba(59, 130, 246, 0.9)',
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        fill: false,
        order: 1
      });
    }

    // Add horizontal line for average (not for binary markets)
    if (!isBinaryMarket && data.length > 0) {
      const avgValue = data.reduce((sum, val) => sum + val, 0) / data.length;
      datasets.push({
        type: 'line',
        label: `Avg (${avgValue.toFixed(1)})`,
        data: Array(data.length).fill(avgValue),
        borderColor: 'rgba(251, 191, 36, 0.9)',
        borderWidth: 2,
        borderDash: [3, 3],
        pointRadius: 0,
        fill: false,
        order: 1
      });
    }

    // Calculate custom averages from played games
    const customAverages = {};
    if (gamesPlayedCount > 0) {
      customAverages.ppg = (playedGames.reduce((sum, g) => sum + (g.pts || 0), 0) / gamesPlayedCount).toFixed(1);
      customAverages.rpg = (playedGames.reduce((sum, g) => sum + (g.reb || 0), 0) / gamesPlayedCount).toFixed(1);
      customAverages.apg = (playedGames.reduce((sum, g) => sum + (g.ast || 0), 0) / gamesPlayedCount).toFixed(1);
      customAverages.spg = (playedGames.reduce((sum, g) => sum + (g.stl || 0), 0) / gamesPlayedCount).toFixed(1);
      customAverages.bpg = (playedGames.reduce((sum, g) => sum + (g.blk || 0), 0) / gamesPlayedCount).toFixed(1);
      customAverages.fg3m = (playedGames.reduce((sum, g) => sum + (g.fg3m || 0), 0) / gamesPlayedCount).toFixed(1);
      customAverages.turnover = (playedGames.reduce((sum, g) => sum + (g.turnover || 0), 0) / gamesPlayedCount).toFixed(1);
      // Combo stats
      customAverages.pra = (playedGames.reduce((sum, g) => sum + (g.pts || 0) + (g.reb || 0) + (g.ast || 0), 0) / gamesPlayedCount).toFixed(1);
      customAverages.pr = (playedGames.reduce((sum, g) => sum + (g.pts || 0) + (g.reb || 0), 0) / gamesPlayedCount).toFixed(1);
      customAverages.pa = (playedGames.reduce((sum, g) => sum + (g.pts || 0) + (g.ast || 0), 0) / gamesPlayedCount).toFixed(1);
      customAverages.ra = (playedGames.reduce((sum, g) => sum + (g.reb || 0) + (g.ast || 0), 0) / gamesPlayedCount).toFixed(1);
      // Double/Triple double hit rates
      const ddCount = playedGames.filter(g => isDoubleDouble(g) === 1).length;
      const tdCount = playedGames.filter(g => isTripleDouble(g) === 1).length;
      customAverages.ddHits = ddCount;
      customAverages.ddRate = ((ddCount / gamesPlayedCount) * 100).toFixed(0);
      customAverages.tdHits = tdCount;
      customAverages.tdRate = ((tdCount / gamesPlayedCount) * 100).toFixed(0);
      // First basket hit rates
      const fbCount = playedGames.filter(g => g.first_fg === true).length;
      customAverages.fbHits = fbCount;
      customAverages.fbRate = ((fbCount / gamesPlayedCount) * 100).toFixed(0);
      // First team basket hit rates
      const ftbCount = playedGames.filter(g => g.first_team_fg === true).length;
      customAverages.ftbHits = ftbCount;
      customAverages.ftbRate = ((ftbCount / gamesPlayedCount) * 100).toFixed(0);
      // First points hit rate (games where first_points > 0)
      const fpCount = playedGames.filter(g => (g.first_points || 0) > 0).length;
      customAverages.fpHits = fpCount;
      customAverages.fpRate = ((fpCount / gamesPlayedCount) * 100).toFixed(0);
      // First team points hit rate (games where first_team_points > 0)
      const ftpCount = playedGames.filter(g => (g.first_team_points || 0) > 0).length;
      customAverages.ftpHits = ftpCount;
      customAverages.ftpRate = ((ftpCount / gamesPlayedCount) * 100).toFixed(0);
    }

    // Get minutes for each game (for overlay)
    const minutesData = sortedGames.map(game => {
      const min = typeof game.min === 'string' ? parseInt(game.min) : game.min;
      return min || 0;
    });

    // Get raw stat values (not transformed for binary markets)
    const rawStatData = sortedGames.map(game => {
      const minutes = typeof game.min === 'string' ? parseInt(game.min) : game.min;
      if (isDoubleDoubleMarket) {
        if (minutes === 0) return 'DNP';
        return isDoubleDouble(game) === 1 ? 'Yes' : 'No';
      }
      if (isTripleDoubleMarket) {
        if (minutes === 0) return 'DNP';
        return isTripleDouble(game) === 1 ? 'Yes' : 'No';
      }
      if (isFirstBasketMarket || isFirstTeamBasketMarket) {
        if (minutes === 0) return 'DNP';
        const fieldName = isFirstBasketMarket ? 'first_fg' : 'first_team_fg';
        return game[fieldName] ? 'Yes' : 'No';
      }
      if (isComboMarket) {
        return getComboStatValue(game, selectedMarketType);
      }
      return game[statField] || 0;
    });

    // Get first_points data for first_basket market tooltip
    const firstPointsData = isFirstBasketMarket ? sortedGames.map(game => {
      const minutes = typeof game.min === 'string' ? parseInt(game.min) : game.min;
      if (minutes === 0) return null;
      return game.first_points || 0;
    }) : null;

    return {
      labels,
      opponents,
      datasets,
      lineValue,
      customAverages,
      gamesPlayedCount,
      isBinaryMarket,
      isFirstBasketMarket,
      isFirstTeamBasketMarket,
      minutesData,
      rawStatData,
      firstPointsData,
      scoreDiffs,
      minuteAvgSeconds,
      hasOnCourtData,
      minuteGameCounts,
    };
  }, [statsData, selectedMarketType, playerData, showDNPs, showFirstPoints, showFirstTeamPoints, showVsOpponent, opponentTeam, playerTeam, showMinutes]);

  // Preload opponent team logos once chartResult is ready. Each Image hooks
  // onload → bump logoLoadTick → re-render → chart redraws with the now-
  // decoded image available to teamLogoPlugin.
  useEffect(() => {
    const opponents = prepareStatsChartData?.opponents;
    if (!opponents || opponents.length === 0) return;
    opponents.forEach((opp) => {
      if (!opp || !opp.abbrev) return;
      const url = getTeamLogoUrl ? getTeamLogoUrl(opp.abbrev) : null;
      if (!url || nbaLogoImageCache.has(url)) return;
      const img = new window.Image();
      img.onload = () => setLogoLoadTick((n) => n + 1);
      img.onerror = () => setLogoLoadTick((n) => n + 1);
      img.src = url;
      nbaLogoImageCache.set(url, img);
    });
  }, [prepareStatsChartData, getTeamLogoUrl]);

  // Chart.js plugin: draws the @/vs text and team logo for each category
  // directly on the canvas, positioned at Chart.js's internal bar centers
  // so alignment is pixel-perfect regardless of column count.
  const LOGO_SIZE = 26;
  // Reserved below-chart space: @/vs text + 26px logo + score-diff row.
  const LOGO_BOTTOM_PAD = 78;
  const teamLogoPlugin = {
    id: 'nbaTeamLogosOnCanvas',
    afterDatasetsDraw: (chart) => {
      const opponents = prepareStatsChartData?.opponents;
      if (!opponents || opponents.length === 0) return;
      const diffs = prepareStatsChartData?.scoreDiffs;
      const { ctx, scales } = chart;
      const xScale = scales.x;
      const yBottom = chart.chartArea?.bottom;
      if (!xScale || yBottom == null) return;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.font = '10px sans-serif';
      opponents.forEach((opp, idx) => {
        if (!opp || !opp.abbrev) return;
        const xPx = xScale.getPixelForValue(idx);
        if (!Number.isFinite(xPx)) return;
        const textY = yBottom + 18;
        ctx.fillStyle = '#6b7280';
        ctx.font = '10px sans-serif';
        ctx.fillText(opp.isHome ? 'vs' : '@', xPx, textY);
        const url = getTeamLogoUrl ? getTeamLogoUrl(opp.abbrev) : null;
        const img = getNbaLogoImage(url);
        if (img && img.complete && img.naturalWidth > 0) {
          ctx.drawImage(img, xPx - LOGO_SIZE / 2, textY + 12, LOGO_SIZE, LOGO_SIZE);
        }
        // Score differential row, directly below the team logo.
        const diff = diffs ? diffs[idx] : null;
        if (diff != null) {
          ctx.font = 'bold 12px sans-serif';
          ctx.fillStyle = diff > 0
            ? 'rgba(34, 197, 94, 0.95)'
            : diff < 0
              ? 'rgba(239, 68, 68, 0.95)'
              : 'rgba(148, 163, 184, 0.9)';
          const text = diff > 0 ? `+${diff}` : `${diff}`;
          ctx.fillText(text, xPx, textY + 12 + LOGO_SIZE + 6);
        }
      });
      ctx.restore();
    },
  };

  // Calculate implied probability from American odds
  const calculateImpliedProbability = (odds) => {
    if (!odds || odds === '-') return null;
    const numericOdds = parseInt(odds);
    if (isNaN(numericOdds)) return null;

    if (numericOdds > 0) {
      // Positive odds: 100 / (odds + 100)
      return (100 / (numericOdds + 100)) * 100;
    } else {
      // Negative odds: |odds| / (|odds| + 100)
      return (Math.abs(numericOdds) / (Math.abs(numericOdds) + 100)) * 100;
    }
  };

  // Handle odds cell click — on desktop this positions a popover tooltip,
  // on mobile it opens a bottom sheet (render below). betMeta carries the
  // full bet shape so the mobile sheet's Add/Remove button can call
  // onOddsClick / onRemoveBet without the caller repeating the betslip
  // wiring on every tap.
  const handleOddsClick = (event, odds, book, point, bestBook, bestBookDiff, lineBooks, betMeta = null) => {
    if (!odds || odds === '-') return;

    const impliedProb = calculateImpliedProbability(odds);
    if (!impliedProb) return;

    // Calculate edge using the SAME logic as getTierStyle for consistency
    let edge = 0;
    if (lineBooks) {
      const allOdds = Object.values(lineBooks)
        .map(b => b.over || b.yes)
        .filter(o => o !== null && o !== undefined);

      if (allOdds.length >= 2) {
        const bestOdds = Math.max(...allOdds);
        const secondBestOdds = Math.max(...allOdds.filter(o => o !== bestOdds));
        const bestOddsCount = allOdds.filter(o => o === bestOdds).length;
        const isBest = odds === bestOdds && bestOddsCount === 1;

        if (isBest) {
          // Handle cross-zero comparison (positive best odds vs negative second best)
          let diff;
          if (bestOdds > 0 && secondBestOdds < 0) {
            const spreadPositive = bestOdds - 100;  // Distance from +100
            const spreadNegative = secondBestOdds - (-100);  // Distance from -100 (will be negative)
            const totalSpread = spreadPositive - spreadNegative;  // Total spread through baseline
            diff = Math.abs(totalSpread / secondBestOdds);
          } else {
            diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
          }
          edge = diff * 100;
        }
      }
    }

    // On mobile, center the tooltip; on desktop, position near cursor
    let x, y;
    if (isMobile) {
      // Center horizontally, position vertically based on click
      x = window.innerWidth / 2 - 100; // Assuming tooltip width ~200px
      y = Math.min(event.clientY, window.innerHeight - 150); // Keep above bottom
    } else {
      x = event.clientX;
      y = event.clientY;
    }

    setTooltipData({
      odds,
      book,
      point,
      impliedProb: impliedProb.toFixed(2),
      edge: edge.toFixed(2),
      x,
      y,
      meta: betMeta,
    });
  };

  // Desktop auto-dismiss on outside click. Mobile uses a bottom sheet with
  // explicit close + Add/Remove — intentionally doesn't auto-dismiss.
  useEffect(() => {
    if (!isMobile && tooltipData) {
      const handleClickOutside = () => setTooltipData(null);
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [tooltipData, isMobile]);

  const fetchPlayerData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/games/nba/${gameId}/${playerId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch player data');
      }

      const data = await response.json();

      // Transform the API data to our component format
      // NBA uses ESPN player IDs
      const nbaPlayerId = playerId?.replace('nba_', '');

      // NBA headshot URL format: https://a.espncdn.com/i/headshots/nba/players/full/{playerId}.png
      const photoUrl = nbaPlayerId
        ? `https://a.espncdn.com/i/headshots/nba/players/full/${nbaPlayerId}.png`
        : null;

      const transformedData = {
        player: {
          name: data.playerName,
          team: data.team,
          teamLogo: data.teamLogo || null, // Include teamLogo from API response if available
          position: playerPosition,
          photo: photoUrl,
          jerseyNumber: null
        },
        markets: []
      };

      // Process NBA markets
      // Points
      if (data.player_points && Array.isArray(data.player_points)) {
        transformedData.markets.push({
          marketKey: 'player_points',
          marketName: 'Points',
          category: 'points',
          lines: processMarketLines(data.player_points, 'player_points')
        });
      }

      // Rebounds
      if (data.player_rebounds && Array.isArray(data.player_rebounds)) {
        transformedData.markets.push({
          marketKey: 'player_rebounds',
          marketName: 'Rebounds',
          category: 'rebounds',
          lines: processMarketLines(data.player_rebounds, 'player_rebounds')
        });
      }

      // Assists
      if (data.player_assists && Array.isArray(data.player_assists)) {
        transformedData.markets.push({
          marketKey: 'player_assists',
          marketName: 'Assists',
          category: 'assists',
          lines: processMarketLines(data.player_assists, 'player_assists')
        });
      }

      // Threes
      if (data.player_threes && Array.isArray(data.player_threes)) {
        transformedData.markets.push({
          marketKey: 'player_threes',
          marketName: 'Threes',
          category: 'threes',
          lines: processMarketLines(data.player_threes, 'player_threes')
        });
      }

      // Points + Rebounds + Assists
      if (data.player_points_rebounds_assists && Array.isArray(data.player_points_rebounds_assists)) {
        transformedData.markets.push({
          marketKey: 'player_points_rebounds_assists',
          marketName: 'PRA',
          category: 'pra',
          lines: processMarketLines(data.player_points_rebounds_assists, 'player_points_rebounds_assists')
        });
      }

      // Points + Rebounds
      if (data.player_points_rebounds && Array.isArray(data.player_points_rebounds)) {
        transformedData.markets.push({
          marketKey: 'player_points_rebounds',
          marketName: 'P+R',
          category: 'points_rebounds',
          lines: processMarketLines(data.player_points_rebounds, 'player_points_rebounds')
        });
      }

      // Points + Assists
      if (data.player_points_assists && Array.isArray(data.player_points_assists)) {
        transformedData.markets.push({
          marketKey: 'player_points_assists',
          marketName: 'P+A',
          category: 'points_assists',
          lines: processMarketLines(data.player_points_assists, 'player_points_assists')
        });
      }

      // Rebounds + Assists
      if (data.player_rebounds_assists && Array.isArray(data.player_rebounds_assists)) {
        transformedData.markets.push({
          marketKey: 'player_rebounds_assists',
          marketName: 'R+A',
          category: 'rebounds_assists',
          lines: processMarketLines(data.player_rebounds_assists, 'player_rebounds_assists')
        });
      }

      // Double Double
      if (data.player_double_double && Array.isArray(data.player_double_double)) {
        transformedData.markets.push({
          marketKey: 'player_double_double',
          marketName: 'Double Double',
          category: 'double_double',
          lines: processMarketLines(data.player_double_double, 'player_double_double')
        });
      }

      // Triple Double
      if (data.player_triple_double && Array.isArray(data.player_triple_double)) {
        transformedData.markets.push({
          marketKey: 'player_triple_double',
          marketName: 'Triple Double',
          category: 'triple_double',
          lines: processMarketLines(data.player_triple_double, 'player_triple_double')
        });
      }

      // First Basket
      if (data.player_first_basket && Array.isArray(data.player_first_basket)) {
        transformedData.markets.push({
          marketKey: 'player_first_basket',
          marketName: 'First Basket',
          category: 'first_basket',
          lines: processMarketLines(data.player_first_basket, 'player_first_basket')
        });
      }

      // First Team Basket
      if (data.player_first_team_basket && Array.isArray(data.player_first_team_basket)) {
        transformedData.markets.push({
          marketKey: 'player_first_team_basket',
          marketName: 'First Team Basket',
          category: 'first_team_basket',
          lines: processMarketLines(data.player_first_team_basket, 'player_first_team_basket')
        });
      }

      setPlayerData(transformedData);
    } catch (err) {
      console.error('Error fetching NBA player data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processMarketLines = (marketLines, marketKey) => {
    // New flat structure - just transform to modal format
    return marketLines.map(line => {
      // Convert flat structure to books object
      const books = {};
      const isBinaryMarket = line.point === 'yes' || line.point === 'no' || line.isBinary === true;

      // Extract all book data from the line
      Object.keys(line).forEach(key => {
        if (key.startsWith(`${marketKey}_`) &&
            !key.includes('bestBook') &&
            !key.includes('nextBook') &&
            !key.includes('Diff') &&
            !key.includes('Price') &&
            !key.includes('Count')) {
          const suffix = key.replace(`${marketKey}_`, '');
          const parts = suffix.split('_');

          if (parts.length >= 2) {
            const bookName = parts.slice(0, -1).join('_');
            const field = parts[parts.length - 1];

            if (field === 'over' || field === 'under' || field === 'yes' || field === 'no' || field === 'point' || field === 'isAlternate' || field === 'isAvailable' || field === 'isMain') {
              if (!books[bookName]) {
                books[bookName] = {};
              }
              books[bookName][field] = line[key];
            }
            // Also capture isAvailable and isMain with _over, _under, or _yes suffix
            if (field === 'over' || field === 'under' || field === 'yes') {
              const availKey = `${marketKey}_${bookName}_${field}_isAvailable`;
              if (line[availKey] !== undefined) {
                books[bookName][`${field}_isAvailable`] = line[availKey];
              }
              const mainKey = `${marketKey}_${bookName}_${field}_isMain`;
              if (line[mainKey] !== undefined) {
                books[bookName][`${field}_isMain`] = line[mainKey];
              }
            }
            // Also capture isAvailable without side suffix (for binary markets).
            // Guard against the case where books[bookName] was never initialized
            // (e.g. when the only field present for this book is something
            // unrecognized like a Secondary-book-specific suffix).
            if (books[bookName] && !books[bookName].isAvailable) {
              const availKey = `${marketKey}_${bookName}_isAvailable`;
              if (line[availKey] !== undefined) {
                books[bookName].isAvailable = line[availKey];
                // Apply to yes_isAvailable for binary markets
                if (books[bookName].yes !== undefined) {
                  books[bookName].yes_isAvailable = line[availKey];
                }
              }
            }
          } else if (parts.length === 1 && isBinaryMarket) {
            // Binary market with direct value (no _yes suffix)
            const bookName = suffix;
            if (!books[bookName]) {
              books[bookName] = {};
            }
            books[bookName].yes = line[key];
          }
        }
      });

      return {
        point: line.point === 'yes' ? null : line.point,
        label: line.point === 'yes' ? 'Yes' : `${line.point}`,
        books: books,
        bestBook: line[`${marketKey}_bestBook`],
        bestBookPrice: line[`${marketKey}_bestBookPrice`],
        bestBookDiff: line[`${marketKey}_bestBookDiff`],
        isSpecial: line.point === 'yes' || line.point === 'no' || line.isBinary === true
      };
    }).sort((a, b) => {
      // Sort special markets (yes/no) to end
      if (a.isSpecial && !b.isSpecial) return 1;
      if (!a.isSpecial && b.isSpecial) return -1;
      return (a.point || 0) - (b.point || 0);
    });
  };

  // Helper function to get tier color styling
  // Get available tiers from the odds history data
  const getAvailableTiers = () => {
    if (!oddsHistoryData || !oddsHistoryData.oddsHistory) return [];

    const tierSet = new Set();
    oddsHistoryData.oddsHistory.forEach(entry => {
      if (entry.point !== undefined && entry.point !== null) {
        tierSet.add(entry.point);
      }
    });

    return Array.from(tierSet).sort((a, b) => a - b);
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!oddsHistoryData || !oddsHistoryData.oddsHistory || oddsHistoryData.oddsHistory.length === 0) {
      return null;
    }

    // Filter by market key and point value
    // For special markets (First Basket, DD, TD), selectedTier will be null
    // For over/under markets, filter by the selected point value
    const filteredHistory = oddsHistoryData.oddsHistory.filter(entry => {
      // First, filter by market key (API uses "marketType" field)
      if (entry.marketType !== chartMarket) return false;

      // Then filter by point value if applicable
      if (selectedTier !== null) {
        return entry.point === selectedTier;
      }

      return true;
    });

    if (filteredHistory.length === 0) {
      return null;
    }

    // Get all unique bookmakers from the filtered data
    const allBookmakers = new Set();
    filteredHistory.forEach(entry => {
      if (entry.books && typeof entry.books === 'object') {
        Object.keys(entry.books).forEach(book => {
          if (entry.books[book]) {
            // Check for over/under markets, yes/no markets, or direct number values
            if (entry.books[book].over !== undefined ||
                entry.books[book].yes !== undefined ||
                typeof entry.books[book] === 'number') {
              allBookmakers.add(book);
            }
          }
        });
      }
    });

    // Include every book; Settings → Graphs only sets the initial hidden flag below.
    const bookmakersArray = Array.from(allBookmakers);

    if (bookmakersArray.length === 0) return null;

    // Group entries by timestamp to handle multiple markets (main + alt) at same time
    // Normalize timestamps to nearest 5 minutes to combine entries that are close in time
    const groupedByTimestamp = {};
    filteredHistory.forEach(entry => {
      if (!entry.timestamp) return;
      const date = new Date(entry.timestamp);
      // Round to nearest 5 minutes
      const minutes = date.getMinutes();
      const roundedMinutes = Math.round(minutes / 5) * 5;
      date.setMinutes(roundedMinutes);
      date.setSeconds(0);
      date.setMilliseconds(0);
      const normalizedTimestamp = date.toISOString();

      if (!groupedByTimestamp[normalizedTimestamp]) {
        groupedByTimestamp[normalizedTimestamp] = [];
      }
      groupedByTimestamp[normalizedTimestamp].push(entry);
    });

    const timestamps = Object.keys(groupedByTimestamp).sort((a, b) => new Date(a) - new Date(b));

    if (timestamps.length === 0) return null;

    // Prepare labels (timestamps) - use unique timestamps
    const labels = timestamps.map(timestamp => {
      try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        return 'Invalid Date';
      }
    });

    // Color palette for bookmakers
    const bookmakerColors = {
      fanduel: '#1A3D6C',
      draftkings: '#5DD335',
      betmgm: '#FFCC00',
      fanatics: '#002F6C',
      espnbet: '#FF0033',
      betrivers: '#EC1C24',
      ballybet: '#000000',
      bovada: '#CC0000',
      caesars: '#000000',
      hardrockbet: '#D4145A'
    };

    // Prepare datasets for each bookmaker
    const datasetsWithLatestValues = bookmakersArray
      .map(bookmaker => {
        const data = timestamps.map(timestamp => {
          const entries = groupedByTimestamp[timestamp];
          const values = [];

          entries.forEach(entry => {
            if (entry.books && entry.books[bookmaker]) {
              let value;

              // For special markets (First Basket, DD, TD), use 'yes' or direct value
              if (selectedTier === null) {
                if (entry.books[bookmaker].yes !== undefined) {
                  value = parseFloat(entry.books[bookmaker].yes);
                } else if (entry.books[bookmaker].over !== undefined) {
                  value = parseFloat(entry.books[bookmaker].over);
                } else if (typeof entry.books[bookmaker] === 'number') {
                  value = parseFloat(entry.books[bookmaker]);
                }
              } else {
                // For over/under markets, use chartIsOver to determine which odds to show
                if (chartIsOver) {
                  if (entry.books[bookmaker].over !== undefined) {
                    value = parseFloat(entry.books[bookmaker].over);
                  } else if (typeof entry.books[bookmaker] === 'number') {
                    value = parseFloat(entry.books[bookmaker]);
                  }
                } else {
                  // Show under odds
                  if (entry.books[bookmaker].under !== undefined) {
                    value = parseFloat(entry.books[bookmaker].under);
                  }
                }
              }

              if (value !== undefined && !isNaN(value)) {
                values.push(value);
              }
            }
          });

          // If multiple values exist (main + alt), use the best (highest) one
          if (values.length > 0) {
            return Math.max(...values);
          }
          return null;
        });

        const hasData = data.some(value => value !== null);
        if (!hasData) return null;

        // Get latest value for sorting
        let latestValue = null;
        for (let i = data.length - 1; i >= 0; i--) {
          if (data[i] !== null) {
            latestValue = data[i];
            break;
          }
        }

        // Calculate point radius
        const pointRadius = data.map((value, index) => {
          if (value === null) return 0;
          if (index === 0) return 3;
          if (index === data.length - 1) return 3;

          let prevValue = null;
          for (let i = index - 1; i >= 0; i--) {
            if (data[i] !== null) {
              prevValue = data[i];
              break;
            }
          }

          return (prevValue !== null && value !== prevValue) ? 3 : 0;
        });

        // Apply forward alias for display (e.g., williamhill_us -> caesars)
        const displayName = BOOKMAKER_ALIASES[bookmaker] || bookmaker;

        return {
          label: displayName.charAt(0).toUpperCase() + displayName.slice(1),
          data: data,
          borderColor: bookmakerColors[bookmaker] || '#999999',
          backgroundColor: bookmakerColors[bookmaker] || '#999999',
          tension: 0.1,
          pointRadius: pointRadius,
          pointHoverRadius: 6,
          spanGaps: true,
          // Initial hidden state from Settings → Graphs.
          hidden: isBookEnabledForGraphs ? !isBookEnabledForGraphs(bookmaker) : false,
          latestValue: latestValue
        };
      })
      .filter(dataset => dataset !== null);

    // Sort datasets by latest value
    const datasets = datasetsWithLatestValues
      .sort((a, b) => {
        const aValue = a.latestValue || -Infinity;
        const bValue = b.latestValue || -Infinity;
        return bValue - aValue;
      })
      .map(dataset => {
        const { latestValue, ...chartDataset } = dataset;
        return chartDataset;
      });

    if (datasets.length === 0 || labels.length === 0) {
      return null;
    }

    return {
      labels,
      datasets
    };
  };

  const chartData = useMemo(() => {
    if (!show) return null;
    return prepareChartData();
  }, [show, oddsHistoryData, selectedTier, showChart, chartIsOver]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    backgroundColor: 'white',
    layout: {
      padding: {
        bottom: 20
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          fontSize: 12
        }
      },
      title: {
        display: true,
        text: `Odds Movement - ${oddsHistoryData?.marketSearch || chartMarket}${selectedTier !== null ? ` (${chartIsOver ? 'Over' : 'Under'} ${selectedTier})` : ''}`,
        font: {
          size: 16
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            if (value === null) return null;
            return `${context.dataset.label}: ${value > 0 ? '+' : ''}${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Odds'
        },
        ticks: {
          callback: function(value) {
            return value > 0 ? `+${value}` : value;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  const getTierStyle = (odds, lineBooks, bestBookDiff) => {
    if (!odds || !lineBooks) return {};

    // Handle both over/under markets and special markets (yes/no).
    // Mirror the server's bestBook selection: skip any book whose odds are
    // marked unavailable (either generic `isAvailable:false` or the
    // side-specific `over_isAvailable`/`yes_isAvailable`). Without this
    // filter an unavailable book that ties the top price makes
    // `bestOddsCount > 1` and no cell gets highlighted — e.g. a stale
    // bet365 price matching fanduel on a first-basket market.
    const extractAvailable = (b) => {
      if (!b || b.isAvailable === false) return null;
      if (b.over !== null && b.over !== undefined) {
        return b.over_isAvailable === false ? null : b.over;
      }
      if (b.yes !== null && b.yes !== undefined) {
        return b.yes_isAvailable === false ? null : b.yes;
      }
      return null;
    };
    let allOdds;
    if (recalcEdgePlayer && bookmakers.length > 0) {
      allOdds = bookmakers
        .map(b => lineBooks[b])
        .map(extractAvailable)
        .filter(o => o !== null && o !== undefined);
    } else {
      allOdds = Object.values(lineBooks)
        .map(extractAvailable)
        .filter(o => o !== null && o !== undefined);
    }

    if (allOdds.length < 2) return {};

    const bestOdds = Math.max(...allOdds);

    // Edge calculation: recalculate from enabled books or use server-provided bestBookDiff
    let diff;
    if (recalcEdgePlayer) {
      const secondBestOdds = Math.max(...allOdds.filter(o => o !== bestOdds));
      if (secondBestOdds === -Infinity) {
        diff = 0;
      } else if (bestOdds > 0 && secondBestOdds < 0) {
        const spreadPositive = bestOdds - 100;
        const spreadNegative = secondBestOdds - (-100);
        const totalSpread = spreadPositive - spreadNegative;
        diff = Math.abs(Math.abs(totalSpread / secondBestOdds));
      } else {
        diff = Math.abs((bestOdds - secondBestOdds) / Math.abs(secondBestOdds));
      }
    } else {
      diff = bestBookDiff !== undefined && bestBookDiff !== null ? Math.abs(bestBookDiff) : 0;
    }

    const bestOddsCount = allOdds.filter(o => o === bestOdds).length;
    const isBest = odds === bestOdds && bestOddsCount === 1;

    let style = {
      cursor: 'pointer',
      fontWeight: isBest ? 'bold' : 'normal'
    };

    if (isBest && allOdds.length > 1) {
      if (diff >= 0.25) {
        // Tier 1: Green with yellow text
        Object.assign(style, {
          backgroundColor: 'green',
          color: 'yellow',
          padding: '2px 6px',
          borderRadius: '3px'
        });
      } else if (diff >= 0.15) {
        // Tier 2: Amber with dark text for better contrast
        Object.assign(style, {
          backgroundColor: '#F59E0B',
          color: '#1E40AF',
          padding: '2px 6px',
          borderRadius: '3px'
        });
      } else if (diff >= 0.05) {
        // Tier 3: Blue - subtle, draws less attention than tier 1 & 2
        Object.assign(style, {
          backgroundColor: '#3B82F6',
          color: 'white',
          padding: '2px 6px',
          borderRadius: '3px'
        });
      } else {
        // Best odds but below tier threshold - use amber text
        Object.assign(style, {
          color: '#F59E0B'
        });
      }
    }

    return style;
  };

  // Desktop: Dynamic orientation based on which dimension is greater
  const renderDesktopTable = (market) => {
    if (!market || !market.lines || market.lines.length === 0) {
      return <p className="text-muted">No odds available for this market.</p>;
    }

    // Separate regular over/under lines from special markets
    const overUnderLines = market.lines.filter(line => !line.isSpecial);
    const specialLines = market.lines.filter(line => line.isSpecial);
    const totalLines = overUnderLines.length + specialLines.length;

    // Determine orientation:
    // - Point value markets (over/under lines) → show as ROWS
    // - Binary markets (special lines only) → show as COLUMNS
    const showLinesAsRows = overUnderLines.length > 0;

    if (showLinesAsRows) {
      // Lines in ROWS, Books in COLUMNS (many point values)
      return (
        <div className="table-responsive">
          <Table bordered hover size="sm">
            <thead className="table-light">
              <tr>
                <th style={{ width: '80px', fontSize: '0.95rem', textAlign: 'center' }}>
                  {showOver && showUnder ? 'O/U' : showOver ? 'Over' : 'Under'}
                </th>
                {bookmakers.map(book => {
                  const bookLink = game?.sgoLinks?.bookmakers?.[book];
                  return (
                    <th key={book} style={{ textAlign: 'center', padding: '8px', fontSize: '0.95rem' }}>
                      {bookLink ? (
                        <a href={bookLink} target="_blank" rel="noopener noreferrer">
                          <img
                            src={getBookLogoUrl(book)}
                            alt={book}
                            style={{ height: '20px', maxWidth: '80px', cursor: 'pointer' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </a>
                      ) : (
                        <img
                          src={getBookLogoUrl(book)}
                          alt={book}
                          style={{ height: '20px', maxWidth: '80px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {overUnderLines.map((line, lineIdx) => (
                <React.Fragment key={`line-${lineIdx}`}>
                  {showOver && (
                    <tr>
                      {showOver && showUnder ? (
                        <td rowSpan={2} style={{
                          fontWeight: 'bold',
                          textAlign: 'center',
                          fontSize: '0.9rem',
                          verticalAlign: 'middle',
                          color: '#007bff',
                          cursor: 'pointer'
                        }}
                        onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                        >
                          {line.point}
                        </td>
                      ) : (
                        <td style={{
                          fontWeight: 'bold',
                          textAlign: 'center',
                          fontSize: '0.9rem',
                          color: '#007bff',
                          cursor: 'pointer'
                        }}
                        onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                        >
                          {line.point}
                        </td>
                      )}
                      {bookmakers.map(book => {
                        const dataKey = getBookmakerDataKey(book);
                        const overOdds = line.books[dataKey]?.over;
                        const cellStyle = getTierStyle(overOdds, line.books, line.bestBookDiff);

                        return (
                          <td
                            key={`${book}-over`}
                            style={{
                              textAlign: 'center',
                              padding: '8px',
                              fontSize: '0.9rem',
                              cursor: overOdds ? 'pointer' : 'default',
                              fontWeight: 'bold',
                              ...cellStyle,
                              opacity: line.books[dataKey]?.over_isAvailable === false ? 0.25 : undefined
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (overOdds) {
                                const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds: overOdds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'over' };
                                handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                if (!isMobile && onOddsClick) onOddsClick(gameId, playerId, playerName, market.marketKey, book, overOdds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'over');
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (!isMobile && overOdds) {
                                handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books);
                              }
                            }}
                            onMouseLeave={() => {
                              if (!isMobile) {
                                setTooltipData(null);
                              }
                            }}
                          >
                            {overOdds ? (overOdds > 0 ? `+${overOdds}` : overOdds) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  {showUnder && (
                    <tr>
                      {!showOver && (
                        <td style={{
                          fontWeight: 'bold',
                          textAlign: 'center',
                          fontSize: '0.9rem',
                          color: '#007bff',
                          cursor: 'pointer'
                        }}
                        onClick={() => fetchOddsHistory(market.marketKey, line.point, false)}
                        >
                          {line.point}
                        </td>
                      )}
                      {bookmakers.map(book => {
                        const dataKey = getBookmakerDataKey(book);
                        const underOdds = line.books[dataKey]?.under;
                        const cellStyle = getTierStyle(underOdds, line.books, line.bestBookDiff);

                        return (
                          <td
                            key={`${book}-under`}
                            style={{
                              textAlign: 'center',
                              padding: '8px',
                              fontSize: '0.9rem',
                              cursor: underOdds ? 'pointer' : 'default',
                              fontWeight: 'bold',
                              ...cellStyle,
                              opacity: line.books[dataKey]?.under_isAvailable === false ? 0.25 : undefined
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (underOdds) {
                                const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds: underOdds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'under' };
                                handleOddsClick(e, underOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                if (!isMobile && onOddsClick) onOddsClick(gameId, playerId, playerName, market.marketKey, book, underOdds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'under');
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (!isMobile && underOdds) {
                                handleOddsClick(e, underOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books);
                              }
                            }}
                            onMouseLeave={() => {
                              if (!isMobile) {
                                setTooltipData(null);
                              }
                            }}
                          >
                            {underOdds ? (underOdds > 0 ? `+${underOdds}` : underOdds) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {specialLines.map((line, lineIdx) => (
                <tr key={`special-${lineIdx}`}>
                  <td
                    style={{
                      fontWeight: 'bold',
                      textAlign: 'center',
                      fontSize: '0.9rem',
                      color: '#007bff',
                      cursor: 'pointer'
                    }}
                    onClick={() => fetchOddsHistory(market.marketKey, null, true)}
                  >
                    {line.label}
                  </td>
                  {bookmakers.map(book => {
                    const dataKey = getBookmakerDataKey(book);
                    const odds = line.books[dataKey]?.yes || line.books[dataKey]?.over;
                    const cellStyle = getTierStyle(odds, line.books, line.bestBookDiff);

                    const isAvailable = line.books[dataKey]?.yes_isAvailable !== false && line.books[dataKey]?.over_isAvailable !== false;
                    return (
                      <td
                        key={`${book}-yes`}
                        style={{
                          textAlign: 'center',
                          padding: '8px',
                          fontSize: '0.9rem',
                          cursor: odds ? 'pointer' : 'default',
                          fontWeight: 'bold',
                          ...cellStyle,
                          opacity: !isAvailable ? 0.25 : undefined
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (odds) {
                            const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'yes' };
                            handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books, betMeta);
                            if (!isMobile && onOddsClick) onOddsClick(gameId, playerId, playerName, market.marketKey, book, odds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'yes');
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile && odds) {
                            handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isMobile) {
                            setTooltipData(null);
                          }
                        }}
                      >
                        {odds ? (odds > 0 ? `+${odds}` : odds) : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );
    }

    // Books in ROWS, Lines in COLUMNS (few point values)

    return (
      <div className="table-responsive">
        <Table bordered hover size="sm">
          <thead className="table-light">
            <tr>
              <th style={{ width: '120px', fontSize: '0.95rem' }}></th>
              {/* Over/Under columns */}
              {overUnderLines.map((line, idx) => (
                <th key={`header-${idx}`} style={{
                  textAlign: 'center',
                  padding: '8px',
                  fontSize: '0.95rem',
                  color: '#007bff',
                  cursor: 'pointer'
                }}
                onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                >
                  {line.label}
                </th>
              ))}
              {/* Special markets columns */}
              {specialLines.map((line, idx) => (
                <th
                  key={`special-${idx}`}
                  style={{
                    textAlign: 'center',
                    padding: '8px',
                    fontSize: '0.95rem',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchOddsHistory(market.marketKey, null, true)}
                >
                  {line.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookmakers.map(book => (
              <tr key={book}>
                <td style={{ padding: '8px', textAlign: 'center', fontSize: '0.9rem' }}>
                  {(() => {
                    const bookLink = game?.sgoLinks?.bookmakers?.[book];
                    return bookLink ? (
                      <a href={bookLink} target="_blank" rel="noopener noreferrer">
                        <img
                          src={getBookLogoUrl(book)}
                          alt={book}
                          style={{ height: '20px', maxWidth: '80px', cursor: 'pointer' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </a>
                    ) : (
                      <img
                        src={getBookLogoUrl(book)}
                        alt={book}
                        style={{ height: '20px', maxWidth: '80px' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    );
                  })()}
                </td>
                {/* Over cells only (matching NHL pattern) */}
                {overUnderLines.map((line, idx) => {
                  const overOdds = line.books[book]?.over;
                  const cellStyle = getTierStyle(overOdds, line.books, line.bestBookDiff);

                  return (
                    <td
                      key={`${book}-${idx}`}
                      style={{
                        textAlign: 'center',
                        padding: '8px',
                        fontSize: '0.9rem',
                        cursor: overOdds ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        ...cellStyle,
                        opacity: line.books[book]?.over_isAvailable === false ? 0.25 : undefined
                      }}
                      onClick={() => {
                        if (overOdds && onOddsClick) {
                          onOddsClick(gameId, playerId, playerName, market.marketKey, book, overOdds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'over');
                        }
                      }}
                    >
                      {overOdds ? (overOdds > 0 ? `+${overOdds}` : overOdds) : '-'}
                    </td>
                  );
                })}
                {/* Special market cells (1st basket, etc) */}
                {specialLines.map((line, idx) => {
                  const dataKey = getBookmakerDataKey(book);
                  const odds = line.books[dataKey]?.yes || line.books[dataKey]?.over;
                  const specialCellStyle = getTierStyle(odds, line.books, line.bestBookDiff);
                  const isAvailable = line.books[dataKey]?.yes_isAvailable !== false && line.books[dataKey]?.over_isAvailable !== false;

                  return (
                    <td
                      key={`special-${book}-${idx}`}
                      style={{
                        textAlign: 'center',
                        padding: '8px',
                        fontSize: '0.9rem',
                        cursor: odds ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        ...specialCellStyle,
                        opacity: !isAvailable ? 0.25 : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (odds) {
                          const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'yes' };
                          handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          if (!isMobile && onOddsClick) onOddsClick(gameId, playerId, playerName, market.marketKey, book, odds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'yes');
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && odds) {
                          handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setTooltipData(null);
                        }
                      }}
                    >
                      {odds ? (odds > 0 ? `+${odds}` : odds) : '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Mobile: Dynamic orientation based on which dimension is greater
  const renderMobileTable = (market) => {
    if (!market || !market.lines || market.lines.length === 0) {
      return <p className="text-muted">No odds available for this market.</p>;
    }

    // Separate regular over/under lines from special markets (1st basket, DD, TD)
    const overUnderLines = market.lines.filter(line => !line.isSpecial);
    const specialLines = market.lines.filter(line => line.isSpecial);
    const totalLines = overUnderLines.length + specialLines.length;

    // Determine orientation:
    // - Point value markets (over/under lines) → show as ROWS
    // - Binary markets (special lines only) → show as COLUMNS
    const showLinesAsRows = overUnderLines.length > 0;

    if (showLinesAsRows) {
      // Lines in ROWS, Books in COLUMNS (many point values)
      return (
        <div style={{ overflowX: 'auto' }}>
          <Table bordered hover size="sm" style={{ minWidth: 'max-content' }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: '60px', minWidth: '60px', textAlign: 'center' }}>
                  {showOver && showUnder ? 'O/U' : showOver ? 'Over' : 'Under'}
                </th>
                {bookmakers.map(book => {
                  const bookLink = game?.sgoLinks?.bookmakers?.[book];
                  return (
                    <th key={book} style={{ width: '70px', minWidth: '70px', textAlign: 'center', padding: '4px' }}>
                      {bookLink ? (
                        <a href={bookLink} target="_blank" rel="noopener noreferrer">
                          <img
                            src={getBookLogoUrl(book)}
                            alt={book}
                            style={{ height: '16px', cursor: 'pointer' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </a>
                      ) : (
                        <img
                          src={getBookLogoUrl(book)}
                          alt={book}
                          style={{ height: '16px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {overUnderLines.map((line, lineIdx) => (
                <React.Fragment key={`line-${lineIdx}`}>
                  {showOver && (
                    <tr>
                      {showOver && showUnder ? (
                        <td rowSpan={2} style={{
                          width: '60px',
                          minWidth: '60px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          color: '#007bff',
                          cursor: 'pointer'
                        }}
                        onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                        >
                          {line.point}
                        </td>
                      ) : (
                        <td style={{
                          width: '60px',
                          minWidth: '60px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          color: '#007bff',
                          cursor: 'pointer'
                        }}
                        onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                        >
                          {line.point}
                        </td>
                      )}
                      {bookmakers.map(book => {
                        const dataKey = getBookmakerDataKey(book);
                        const overOdds = line.books[dataKey]?.over;
                        const cellStyle = getTierStyle(overOdds, line.books, line.bestBookDiff);

                        return (
                          <td
                            key={`${book}-over`}
                            style={{
                              width: '70px',
                              minWidth: '70px',
                              textAlign: 'center',
                              cursor: overOdds ? 'pointer' : 'default',
                              fontWeight: 'bold',
                              ...cellStyle,
                              opacity: line.books[dataKey]?.over_isAvailable === false ? 0.25 : undefined
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (overOdds) {
                                const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds: overOdds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'over' };
                                handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                if (!isMobile && onOddsClick) onOddsClick(gameId, playerId, playerName, market.marketKey, book, overOdds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'over');
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (!isMobile && overOdds) {
                                handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books);
                              }
                            }}
                            onMouseLeave={() => {
                              if (!isMobile) {
                                setTooltipData(null);
                              }
                            }}
                          >
                            {overOdds ? (overOdds > 0 ? `+${overOdds}` : overOdds) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  {showUnder && (
                    <tr>
                      {!showOver && (
                        <td style={{
                          width: '60px',
                          minWidth: '60px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          color: '#007bff',
                          cursor: 'pointer'
                        }}
                        onClick={() => fetchOddsHistory(market.marketKey, line.point, false)}
                        >
                          {line.point}
                        </td>
                      )}
                      {bookmakers.map(book => {
                        const dataKey = getBookmakerDataKey(book);
                        const underOdds = line.books[dataKey]?.under;
                        const cellStyle = getTierStyle(underOdds, line.books, line.bestBookDiff);

                        return (
                          <td
                            key={`${book}-under`}
                            style={{
                              width: '70px',
                              minWidth: '70px',
                              textAlign: 'center',
                              cursor: underOdds ? 'pointer' : 'default',
                              fontWeight: 'bold',
                              ...cellStyle,
                              opacity: line.books[dataKey]?.under_isAvailable === false ? 0.25 : undefined
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (underOdds) {
                                const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds: underOdds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'under' };
                                handleOddsClick(e, underOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                                if (!isMobile && onOddsClick) onOddsClick(gameId, playerId, playerName, market.marketKey, book, underOdds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'under');
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (!isMobile && underOdds) {
                                handleOddsClick(e, underOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books);
                              }
                            }}
                            onMouseLeave={() => {
                              if (!isMobile) {
                                setTooltipData(null);
                              }
                            }}
                          >
                            {underOdds ? (underOdds > 0 ? `+${underOdds}` : underOdds) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {specialLines.map((line, lineIdx) => (
                <tr key={`special-${lineIdx}`}>
                  <td
                    style={{
                      width: '60px',
                      minWidth: '60px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      color: '#007bff',
                      cursor: 'pointer'
                    }}
                    onClick={() => fetchOddsHistory(market.marketKey, null, true)}
                  >
                    {line.label}
                  </td>
                  {bookmakers.map(book => {
                    const dataKey = getBookmakerDataKey(book);
                    const odds = line.books[dataKey]?.yes || line.books[dataKey]?.over;
                    const cellStyle = getTierStyle(odds, line.books, line.bestBookDiff);
                    const isAvailable = line.books[dataKey]?.yes_isAvailable !== false && line.books[dataKey]?.over_isAvailable !== false;

                    return (
                      <td
                        key={`${book}-yes`}
                        style={{
                          width: '70px',
                          minWidth: '70px',
                          textAlign: 'center',
                          cursor: odds ? 'pointer' : 'default',
                          fontWeight: 'bold',
                          ...cellStyle,
                          opacity: !isAvailable ? 0.25 : undefined
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (odds) {
                            const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'yes' };
                            handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books, betMeta);
                            if (!isMobile && onOddsClick) onOddsClick(gameId, playerId, playerName, market.marketKey, book, odds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'yes');
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile && odds) {
                            handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isMobile) {
                            setTooltipData(null);
                          }
                        }}
                      >
                        {odds ? (odds > 0 ? `+${odds}` : odds) : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );
    }

    // Books in ROWS, Lines in COLUMNS (few point values)
    return (
      <div style={{ overflowX: 'auto' }}>
        <Table bordered hover size="sm" style={{ minWidth: 'max-content' }}>
          <thead className="table-light">
            <tr>
              <th style={{ width: '60px', minWidth: '60px' }}></th>
              {overUnderLines.map((line, idx) => (
                <th key={`header-${idx}`} style={{
                  width: '70px',
                  minWidth: '70px',
                  textAlign: 'center',
                  padding: '4px',
                  color: '#007bff',
                  cursor: 'pointer'
                }}
                onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                >
                  {line.label}
                </th>
              ))}
              {specialLines.map((line, idx) => (
                <th
                  key={`special-${idx}`}
                  style={{
                    width: '70px',
                    minWidth: '70px',
                    textAlign: 'center',
                    padding: '4px',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchOddsHistory(market.marketKey, null, true)}
                >
                  {line.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookmakers.map(book => (
              <tr key={book}>
                <td style={{ width: '60px', minWidth: '60px', textAlign: 'center' }}>
                  {(() => {
                    const bookLink = game?.sgoLinks?.bookmakers?.[book];
                    return bookLink ? (
                      <a href={bookLink} target="_blank" rel="noopener noreferrer">
                        <img
                          src={getBookLogoUrl(book)}
                          alt={book}
                          style={{ height: '16px', cursor: 'pointer' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </a>
                    ) : (
                      <img
                        src={getBookLogoUrl(book)}
                        alt={book}
                        style={{ height: '16px' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    );
                  })()}
                </td>
                {overUnderLines.map((line, idx) => {
                  const overOdds = line.books[book]?.over;
                  const cellStyle = getTierStyle(overOdds, line.books, line.bestBookDiff);

                  return (
                    <td
                      key={`${book}-${idx}`}
                      style={{
                        width: '70px',
                        minWidth: '70px',
                        textAlign: 'center',
                        cursor: overOdds ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        ...cellStyle,
                        opacity: line.books[book]?.over_isAvailable === false ? 0.25 : undefined
                      }}
                      onClick={() => {
                        if (overOdds && onOddsClick) {
                          onOddsClick(gameId, playerId, playerName, market.marketKey, book, overOdds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'over');
                        }
                      }}
                    >
                      {overOdds ? (overOdds > 0 ? `+${overOdds}` : overOdds) : '-'}
                    </td>
                  );
                })}
                {specialLines.map((line, idx) => {
                  const odds = line.books[book]?.yes || line.books[book]?.over;
                  const cellStyle = getTierStyle(odds, line.books, line.bestBookDiff);
                  const isAvailable = line.books[book]?.yes_isAvailable !== false && line.books[book]?.over_isAvailable !== false;

                  return (
                    <td
                      key={`special-${book}-${idx}`}
                      style={{
                        width: '70px',
                        minWidth: '70px',
                        textAlign: 'center',
                        cursor: odds ? 'pointer' : 'default',
                        fontWeight: 'bold',
                        ...cellStyle,
                        opacity: !isAvailable ? 0.25 : undefined
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (odds) {
                          const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'yes' };
                          handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          if (!isMobile && onOddsClick) onOddsClick(gameId, playerId, playerName, market.marketKey, book, odds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'yes');
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile && odds) {
                          handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile) {
                          setTooltipData(null);
                        }
                      }}
                    >
                      {odds ? (odds > 0 ? `+${odds}` : odds) : '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const currentMarket = playerData?.markets.find(m => m.category === selectedMarketType);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={modalSize}
      fullscreen="sm-down"
      centered
      className="nba-player-modal"
      backdropClassName="player-modal-backdrop"
    >
      {isMobile && <ModalBrandingBar />}
      <Modal.Header closeButton style={{ borderBottom: '2px solid #dee2e6' }}>
        <Modal.Title className="w-100">
          {playerData?.player && (
            <div className="d-flex align-items-center gap-3">
              <img
                src={playerData.player.photo}
                alt={playerName}
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #dee2e6'
                }}
                onError={(e) => {
                  if (e.target.src !== 'https://via.placeholder.com/60x60?text=No+Photo') {
                    e.target.src = 'https://via.placeholder.com/60x60?text=No+Photo';
                  }
                }}
              />
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">{playerName}</h5>
                  {playerData.player.jerseyNumber && (
                    <Badge bg="secondary">#{playerData.player.jerseyNumber}</Badge>
                  )}
                </div>
                <div className="text-muted small d-flex align-items-center gap-2">
                  <img
                    src={playerData.player.teamLogo || getTeamLogoUrl(playerTeam)}
                    alt={playerTeam}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span>{playerTeam} • {playerPosition}</span>
                  <AddToScratchpadLink
                    playerData={{ gameId, playerId, playerName, playerTeam, awayTeam: game?.away, homeTeam: game?.home }}
                    onToggleScratchpad={onToggleScratchpad}
                    isInScratchpad={isInScratchpad}
                  />
                </div>
              </div>
            </div>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: isMobile ? '85vh' : '70vh', overflowY: 'auto' }}>
        {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <Spinner animation="border" />
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <h5>Error loading player data</h5>
            <p>{error}</p>
            <button className="btn btn-primary btn-sm" onClick={fetchPlayerData}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && playerData && !showChart && (
          <>
            {/* Market tabs - always show all markets */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              {['points', 'rebounds', 'assists', 'threes', 'pra', 'points_rebounds', 'points_assists', 'rebounds_assists', 'double_double', 'triple_double', 'first_basket', 'first_team_basket'].map(category => {
                const market = playerData.markets.find(m => m.category === category);
                const hasData = !!market;
                const marketName = category === 'points' ? 'Points'
                  : category === 'rebounds' ? 'Rebounds'
                  : category === 'assists' ? 'Assists'
                  : category === 'threes' ? 'Threes'
                  : category === 'pra' ? 'PRA'
                  : category === 'points_rebounds' ? 'P+R'
                  : category === 'points_assists' ? 'P+A'
                  : category === 'rebounds_assists' ? 'R+A'
                  : category === 'double_double' ? 'DD'
                  : category === 'triple_double' ? 'TD'
                  : category === 'first_basket' ? '1st'
                  : category === 'first_team_basket' ? '1st Tm'
                  : category;

                return (
                  <button
                    key={category}
                    className={`btn btn-sm ${selectedMarketType === category ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => hasData && setSelectedMarketType(category)}
                    disabled={!hasData}
                    style={{
                      opacity: hasData ? 1 : 0.5,
                      cursor: hasData ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {marketName}
                  </button>
                );
              })}
            </div>

            {/* Over/Under toggles for O/U markets */}
            {currentMarket && currentMarket.lines && currentMarket.lines.some(line => !line.isSpecial) && (
              <div className="d-flex gap-2 mb-3 align-items-center">
                <span className="text-muted small">Show:</span>
                <button
                  className={`btn btn-sm ${showOver ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => setShowOver(!showOver)}
                >
                  Over
                </button>
                <button
                  className={`btn btn-sm ${showUnder ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => setShowUnder(!showUnder)}
                >
                  Under
                </button>
              </div>
            )}

            {/* Market table */}
            {currentMarket ? (
              <>
                {isMobile ? renderMobileTable(currentMarket) : renderDesktopTable(currentMarket)}

                {/* Stats bar chart */}
                {prepareStatsChartData && (
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#1a1a2e',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '8px', flexWrap: 'wrap' }}>
                      <h6 style={{ margin: 0, color: '#e2e8f0' }}>
                        Game Log - {selectedMarketType.replace('player_', '').replace(/_/g, ' ').toUpperCase()}
                      </h6>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {prepareStatsChartData.isBinaryMarket ? (
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                          {(() => {
                            const avg = prepareStatsChartData.customAverages;
                            if (!avg) return '-';
                            if (selectedMarketType === 'double_double') {
                              return `${avg.ddHits}/${prepareStatsChartData.gamesPlayedCount} (${avg.ddRate}%)`;
                            }
                            if (selectedMarketType === 'triple_double') {
                              return `${avg.tdHits}/${prepareStatsChartData.gamesPlayedCount} (${avg.tdRate}%)`;
                            }
                            if (selectedMarketType === 'first_basket' || selectedMarketType === 'player_first_basket') {
                              const fpInfo = showFirstPoints ? ` | ${avg.fpRate}% 1st Points (${avg.fpHits}/${prepareStatsChartData.gamesPlayedCount})` : '';
                              return `${avg.fbRate}% First Baskets (${avg.fbHits}/${prepareStatsChartData.gamesPlayedCount})${fpInfo}`;
                            }
                            if (selectedMarketType === 'first_team_basket' || selectedMarketType === 'player_first_team_basket') {
                              const ftpInfo = showFirstTeamPoints ? ` | ${avg.ftpRate}% 1st Tm Points (${avg.ftpHits}/${prepareStatsChartData.gamesPlayedCount})` : '';
                              return `${avg.ftbRate}% First Team Baskets (${avg.ftbHits}/${prepareStatsChartData.gamesPlayedCount})${ftpInfo}`;
                            }
                            return '-';
                          })()}
                        </span>
                      ) : prepareStatsChartData.lineValue && (
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                          Line: {prepareStatsChartData.lineValue} | Avg: {
                            (() => {
                              const avg = prepareStatsChartData.customAverages;
                              if (!avg) return '-';
                              if (selectedMarketType === 'points') return avg.ppg;
                              if (selectedMarketType === 'assists') return avg.apg;
                              if (selectedMarketType === 'rebounds') return avg.rpg;
                              if (selectedMarketType === 'steals') return avg.spg;
                              if (selectedMarketType === 'blocks') return avg.bpg;
                              if (selectedMarketType === 'threes') return avg.fg3m;
                              if (selectedMarketType === 'turnovers') return avg.turnover;
                              if (selectedMarketType === 'pra' || selectedMarketType === 'points_rebounds_assists') return avg.pra;
                              if (selectedMarketType === 'points_rebounds') return avg.pr;
                              if (selectedMarketType === 'points_assists') return avg.pa;
                              if (selectedMarketType === 'rebounds_assists') return avg.ra;
                              return '-';
                            })()
                          } ({prepareStatsChartData.gamesPlayedCount} games)
                        </span>
                      )}
                      {(() => {
                        const currentYear = getDefaultNbaStatsYear();
                        const prevYear = String(parseInt(currentYear, 10) - 1);
                        return (
                          <div className="btn-group btn-group-sm" role="group">
                            <button
                              type="button"
                              className={`btn ${statsYear === currentYear ? 'btn-warning' : 'btn-outline-warning'}`}
                              onClick={() => setStatsYear(currentYear)}
                              style={{ fontSize: '0.65rem', padding: '1px 6px' }}
                            >
                              Current
                            </button>
                            <button
                              type="button"
                              className={`btn ${statsYear === prevYear ? 'btn-warning' : 'btn-outline-warning'}`}
                              onClick={() => setStatsYear(prevYear)}
                              style={{ fontSize: '0.65rem', padding: '1px 6px' }}
                            >
                              Last
                            </button>
                          </div>
                        );
                      })()}
                      </div>
                    </div>
                    <div
                      className="dark-scrollbar"
                      style={{
                        overflowX: 'auto',
                        overflowY: 'hidden'
                      }}
                    >
                      {(() => {
                        // Natural chart width: y-axis (32) + per-column
                        // (bar + gap, 44) + right pad (8). Fixed, not min,
                        // so a chart with a handful of games stays tightly
                        // packed instead of stretching to fill the modal.
                        const perColumn = 44;
                        const chartWidthPx = 32 + prepareStatsChartData.labels.length * perColumn + 8;
                        const chartHeightPx = (isMobile ? 220 : 270) + LOGO_BOTTOM_PAD;
                        return (
                      <div style={{
                        height: `${chartHeightPx}px`,
                        width: `${chartWidthPx}px`,
                        margin: '0 auto'
                      }}>
                        <Bar
                          key={`stats-chart-${selectedMarketType}-${showMinutes}-${showDNPs}-${showVsOpponent}-${statsYear}-${prepareStatsChartData.labels.length}-${prepareStatsChartData.datasets.length}-${logoLoadTick}`}
                          data={prepareStatsChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            layout: { padding: { bottom: LOGO_BOTTOM_PAD } },
                            plugins: {
                              legend: {
                                display: true,
                                position: 'top',
                                labels: {
                                  usePointStyle: true,
                                  padding: 10,
                                  font: {
                                    size: 10
                                  },
                                  color: '#94a3b8',
                                  filter: (legendItem) => legendItem.text.includes('Line') || legendItem.text.includes('Avg')
                                }
                              },
                              tooltip: {
                                callbacks: {
                                  title: (items) => {
                                    const first = items[0];
                                    const label = first?.label;
                                    const dateStr = Array.isArray(label) ? label.join(' ') : (label || '');
                                    const opp = prepareStatsChartData.opponents?.[first?.dataIndex];
                                    if (!opp || !opp.abbrev) return dateStr;
                                    return `${dateStr} ${opp.isHome ? 'vs' : '@'}${opp.abbrev}`;
                                  },
                                  label: (item) => {
                                    if (item.dataset.type === 'line') {
                                      return `Line: ${item.raw}`;
                                    }
                                    const idx = item.dataIndex;
                                    const minutes = prepareStatsChartData.minutesData?.[idx] || 0;
                                    const statVal = prepareStatsChartData.rawStatData?.[idx];
                                    if (prepareStatsChartData.isBinaryMarket) {
                                      return [`${statVal}`, `${minutes} min`];
                                    }
                                    return [`${statVal} ${selectedMarketType.replace('player_', '').replace(/_/g, ' ')}`, `${minutes} min`];
                                  }
                                }
                              }
                            },
                            scales: {
                              x: {
                                ticks: {
                                  maxRotation: 0,
                                  minRotation: 0,
                                  font: {
                                    size: isMobile ? 9 : 10
                                  },
                                  autoSkip: false,
                                  color: '#94a3b8'
                                },
                                grid: {
                                  color: 'rgba(148, 163, 184, 0.1)'
                                }
                              },
                              y: {
                                beginAtZero: true,
                                max: prepareStatsChartData.isBinaryMarket ? 2.5 : undefined,
                                // Pin the y-axis width so the minutes chart
                                // below can share the same bar x-centers.
                                afterFit: (axis) => { axis.width = 32; },
                                ticks: {
                                  stepSize: prepareStatsChartData.isBinaryMarket ? 1 : 1,
                                  color: '#94a3b8',
                                  callback: function(value) {
                                    if (prepareStatsChartData.isBinaryMarket) {
                                      if (value === 2) return 'Yes';
                                      if (value === 1) return 'No';
                                      return '';
                                    }
                                    return value;
                                  }
                                },
                                grid: {
                                  color: 'rgba(148, 163, 184, 0.2)',
                                  display: !prepareStatsChartData.isBinaryMarket
                                }
                              }
                            }
                          }}
                          plugins={[teamLogoPlugin]}
                        />
                      </div>
                        );
                      })()}
                    </div>
                    {/* Legend and toggles */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '12px',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {/* Legend */}
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                        <span style={{ color: 'rgb(34, 197, 94)', marginRight: '15px' }}>■ {
                          prepareStatsChartData.isBinaryMarket
                            ? (prepareStatsChartData.isFirstBasketMarket ? '1st FG'
                              : prepareStatsChartData.isFirstTeamBasketMarket ? '1st Tm FG'
                              : 'Yes')
                            : 'Over'
                        }</span>
                        {/* Hide No legend for first basket/team basket markets since bars are tiny */}
                        {!prepareStatsChartData.isFirstBasketMarket && !prepareStatsChartData.isFirstTeamBasketMarket && (
                          <span style={{ color: 'rgb(239, 68, 68)', marginRight: '15px' }}>■ {prepareStatsChartData.isBinaryMarket ? 'No' : 'Under'}</span>
                        )}
                        {/* Show 1st Points legend when toggle is on */}
                        {prepareStatsChartData.isFirstBasketMarket && showFirstPoints && (
                          <span style={{ color: 'rgba(148, 163, 184, 0.7)' }}>■ 1st Points</span>
                        )}
                        {/* Show 1st Team Points legend when toggle is on */}
                        {prepareStatsChartData.isFirstTeamBasketMarket && showFirstTeamPoints && (
                          <span style={{ color: 'rgba(148, 163, 184, 0.7)' }}>■ 1st Tm Points</span>
                        )}
                      </div>

                      {/* Toggles */}
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        {/* Show Minutes Toggle - only for non-binary markets */}
                        {!prepareStatsChartData.isBinaryMarket && (
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          color: '#94a3b8'
                        }}>
                          <div
                            onClick={() => setShowMinutes(!showMinutes)}
                            style={{
                              width: '36px',
                              height: '20px',
                              borderRadius: '10px',
                              backgroundColor: showMinutes ? '#3b82f6' : '#4b5563',
                              position: 'relative',
                              transition: 'background-color 0.2s',
                              cursor: 'pointer'
                            }}
                          >
                            <div style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: 'white',
                              position: 'absolute',
                              top: '2px',
                              left: showMinutes ? '18px' : '2px',
                              transition: 'left 0.2s'
                            }} />
                          </div>
                          Min
                        </label>
                        )}

                        {/* Show DNPs Toggle */}
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          color: '#94a3b8'
                        }}>
                          <div
                            onClick={() => setShowDNPs(!showDNPs)}
                            style={{
                              width: '36px',
                              height: '20px',
                              borderRadius: '10px',
                              backgroundColor: showDNPs ? '#f59e0b' : '#4b5563',
                              position: 'relative',
                              transition: 'background-color 0.2s',
                              cursor: 'pointer'
                            }}
                          >
                            <div style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: 'white',
                              position: 'absolute',
                              top: '2px',
                              left: showDNPs ? '18px' : '2px',
                              transition: 'left 0.2s'
                            }} />
                          </div>
                          DNPs
                        </label>

                        {/* Show vs Opponent Toggle - only when opponent is known */}
                        {opponentTeam && (
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            color: '#94a3b8'
                          }}>
                            <div
                              onClick={() => setShowVsOpponent(!showVsOpponent)}
                              style={{
                                width: '36px',
                                height: '20px',
                                borderRadius: '10px',
                                backgroundColor: showVsOpponent ? '#10b981' : '#4b5563',
                                position: 'relative',
                                transition: 'background-color 0.2s',
                                cursor: 'pointer'
                              }}
                            >
                              <div style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                position: 'absolute',
                                top: '2px',
                                left: showVsOpponent ? '18px' : '2px',
                                transition: 'left 0.2s'
                              }} />
                            </div>
                            vs {opponentTeam}
                          </label>
                        )}

                        {/* Show 1st Points Toggle - only for first_basket market */}
                        {prepareStatsChartData.isFirstBasketMarket && (
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            color: '#94a3b8'
                          }}>
                            <div
                              onClick={() => setShowFirstPoints(!showFirstPoints)}
                              style={{
                                width: '36px',
                                height: '20px',
                                borderRadius: '10px',
                                backgroundColor: showFirstPoints ? '#3b82f6' : '#4b5563',
                                position: 'relative',
                                transition: 'background-color 0.2s',
                                cursor: 'pointer'
                              }}
                            >
                              <div style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                position: 'absolute',
                                top: '2px',
                                left: showFirstPoints ? '18px' : '2px',
                                transition: 'left 0.2s'
                              }} />
                            </div>
                            1st Points
                          </label>
                        )}

                        {/* Show 1st Team Points Toggle - only for first_team_basket market */}
                        {prepareStatsChartData.isFirstTeamBasketMarket && (
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            color: '#94a3b8'
                          }}>
                            <div
                              onClick={() => setShowFirstTeamPoints(!showFirstTeamPoints)}
                              style={{
                                width: '36px',
                                height: '20px',
                                borderRadius: '10px',
                                backgroundColor: showFirstTeamPoints ? '#3b82f6' : '#4b5563',
                                position: 'relative',
                                transition: 'background-color 0.2s',
                                cursor: 'pointer'
                              }}
                            >
                              <div style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                position: 'absolute',
                                top: '2px',
                                left: showFirstTeamPoints ? '18px' : '2px',
                                transition: 'left 0.2s'
                              }} />
                            </div>
                            1st Tm Points
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Minutes Chart - shown below toggles when Min toggle is on */}
                    {showMinutes && !prepareStatsChartData.isBinaryMarket && (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#94a3b8',
                          marginBottom: '8px',
                          textAlign: 'center'
                        }}>
                          Minutes Played
                        </div>
                        <div
                          className="dark-scrollbar"
                          style={{
                            overflowX: 'auto',
                            overflowY: 'hidden'
                          }}
                        >
                          {(() => {
                            // Mirror the prop chart's natural width + pinned
                            // y-axis so every minutes bar sits directly under
                            // its corresponding prop bar.
                            const perColumn = 44;
                            const chartWidthPx = 32 + prepareStatsChartData.labels.length * perColumn + 8;
                            return (
                          <div style={{
                            height: '140px',
                            width: `${chartWidthPx}px`,
                            margin: '0 auto'
                          }}>
                            <Bar
                              // Remount on market/season change so Chart.js
                              // tooltip can't hold stale `active` refs
                              // across label-count changes.
                              key={`minutes-chart-${selectedMarketType}-${statsYear}-${prepareStatsChartData.labels.length}`}
                              data={{
                                labels: prepareStatsChartData.labels,
                                datasets: [{
                                  type: 'bar',
                                  label: 'Minutes',
                                  data: prepareStatsChartData.minutesData,
                                  backgroundColor: 'rgba(100, 116, 139, 0.7)',
                                  borderColor: 'rgba(100, 116, 139, 0.9)',
                                  borderWidth: 1,
                                  barThickness: 30
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: { display: false },
                                  tooltip: {
                                    callbacks: {
                                      title: (items) => {
                                        const label = items[0]?.label;
                                        return Array.isArray(label) ? label.join(' ') : label || '';
                                      },
                                      label: (item) => `${item.raw} min`
                                    }
                                  }
                                },
                                scales: {
                                  x: {
                                    display: false
                                  },
                                  y: {
                                    beginAtZero: true,
                                    // Pin to the same width as the prop
                                    // chart's y-axis so bar x-centers align
                                    // exactly between the two charts.
                                    afterFit: (axis) => { axis.width = 32; },
                                    ticks: {
                                      color: '#94a3b8',
                                      stepSize: 10
                                    },
                                    grid: {
                                      color: 'rgba(148, 163, 184, 0.1)'
                                    }
                                  }
                                }
                              }}
                            />
                          </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Average on-court seconds per minute 1..48 across the
                        filtered games. Line chart with dashed vertical
                        dividers at the end of each quarter (minutes 12, 24,
                        36). Independent width from the two charts above since
                        the x-axis is a fixed 48-minute scale. */}
                    {prepareStatsChartData.hasOnCourtData && (() => {
                      const quarterDividersPlugin = {
                        id: 'quarterDividers',
                        afterDatasetsDraw: (chart) => {
                          const { ctx, scales, chartArea } = chart;
                          if (!scales?.x || !chartArea) return;
                          ctx.save();
                          ctx.strokeStyle = 'rgba(148, 163, 184, 0.55)';
                          ctx.lineWidth = 1;
                          ctx.setLineDash([4, 4]);
                          [11, 23, 35].forEach(idx => {
                            const a = scales.x.getPixelForValue(idx);
                            const b = scales.x.getPixelForValue(idx + 1);
                            const x = (a + b) / 2;
                            ctx.beginPath();
                            ctx.moveTo(x, chartArea.top);
                            ctx.lineTo(x, chartArea.bottom);
                            ctx.stroke();
                          });
                          ctx.restore();
                        },
                      };
                      return (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#94a3b8',
                          marginBottom: '8px',
                          textAlign: 'center'
                        }}>
                          Avg Court Time by Game Minute (sec)
                        </div>
                        <div style={{ height: '140px', width: '100%' }}>
                          <Line
                            key={`court-min-chart-${statsYear}-${showDNPs}-${showVsOpponent}`}
                            data={{
                              labels: Array.from({ length: 48 }, (_, i) => String(i + 1)),
                              datasets: [{
                                label: 'Avg Sec On Court',
                                data: prepareStatsChartData.minuteAvgSeconds,
                                borderColor: 'rgba(59, 130, 246, 0.95)',
                                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                borderWidth: 2,
                                tension: 0.3,
                                pointRadius: 0,
                                pointHoverRadius: 4,
                                fill: true,
                              }]
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              interaction: { mode: 'index', intersect: false },
                              plugins: {
                                legend: { display: false },
                                tooltip: {
                                  callbacks: {
                                    title: (items) => {
                                      const min = parseInt(items[0]?.label, 10);
                                      const q = Math.floor((min - 1) / 12) + 1;
                                      return `Minute ${min} (Q${q})`;
                                    },
                                    label: (item) => {
                                      const n = typeof item.raw === 'number' ? item.raw : 0;
                                      const games = prepareStatsChartData.minuteGameCounts?.[item.dataIndex] || 0;
                                      return [`${n.toFixed(1)} sec avg`, `across ${games} game${games === 1 ? '' : 's'}`];
                                    }
                                  }
                                }
                              },
                              scales: {
                                x: {
                                  ticks: {
                                    color: '#94a3b8',
                                    font: { size: 9 },
                                    autoSkip: false,
                                    callback: function(value, index) {
                                      const m = index + 1;
                                      if (m === 1 || m === 12 || m === 24 || m === 36 || m === 48) return String(m);
                                      return '';
                                    }
                                  },
                                  grid: { display: false }
                                },
                                y: {
                                  beginAtZero: true,
                                  max: 60,
                                  display: false,
                                }
                              }
                            }}
                            plugins={[quarterDividersPlugin]}
                          />
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                )}

                {!prepareStatsChartData && statsData && (
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#1a1a2e',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#94a3b8'
                  }}>
                    No stats available for this market type
                  </div>
                )}

                {/* Helper text for chart feature */}
                <div style={{
                  textAlign: 'center',
                  marginTop: '12px',
                  fontSize: '0.85rem',
                  color: '#6c757d',
                  fontStyle: 'italic'
                }}>
                  📊 Click a {currentMarket.lines && currentMarket.lines.some(line => !line.isSpecial) ? 'point value' : 'market label'} to view odds movement
                </div>
              </>
            ) : (
              <p className="text-muted">Select a market to view odds.</p>
            )}
          </>
        )}

        {!loading && !error && showChart && (
          <>
            {/* Back Button */}
            <div className="mb-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleBackToTable}
              >
                ← Back to Odds Table
              </Button>
            </div>

            {loadingChart && (
              <div className="d-flex justify-content-center align-items-center" style={{ height: isMobile ? '70vh' : '400px' }}>
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading chart...</span>
                </Spinner>
              </div>
            )}

            {!loadingChart && chartData && chartData.datasets && chartData.datasets.length > 0 && chartData.labels && chartData.labels.length > 0 ? (
              <div style={{
                height: isMobile ? '70vh' : '400px',
                position: 'relative',
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                <Line
                  key={`chart-${gameId}-${playerId}-${chartMarket}-${chartPoint}`}
                  data={chartData}
                  options={{
                    ...chartOptions,
                    animation: {
                      duration: 0
                    },
                    plugins: {
                      ...chartOptions.plugins,
                      title: {
                        ...chartOptions.plugins.title,
                        font: {
                          size: isMobile ? 14 : 16
                        }
                      },
                      legend: {
                        position: 'bottom',
                        labels: {
                          boxWidth: isMobile ? 10 : 12,
                          fontSize: isMobile ? 11 : 12
                        }
                      }
                    }
                  }}
                  plugins={[]}
                />
              </div>
            ) : !loadingChart && (
              <div className="alert alert-warning" role="alert">
                No odds movement data available for this market.
              </div>
            )}
          </>
        )}
      </Modal.Body>

      {/* Added to betslip notification */}
      {showAddedToSlip && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#198754',
            color: 'white',
            padding: '8px 20px',
            borderRadius: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 10000,
            fontSize: '0.85rem',
            fontWeight: 'bold',
            pointerEvents: 'none',
            animation: 'fadeIn 0.2s ease'
          }}
        >
          Added to betslip ({betslipCount})
        </div>
      )}

      {/* Desktop tooltip (hover popover) */}
      {tooltipData && !isMobile && (
        <div
          style={{
            position: 'fixed',
            left: `${tooltipData.x + 10}px`,
            top: `${tooltipData.y + 10}px`,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 9999,
            fontSize: '0.85rem',
            minWidth: '200px',
            pointerEvents: 'none'
          }}
        >
          <div style={{ marginBottom: '8px', fontWeight: 'bold', borderBottom: '1px solid #4b5563', paddingBottom: '6px' }}>
            {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1)} ({tooltipData.point})
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Odds:</span>
            <span style={{ fontWeight: 'bold' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Implied Prob:</span>
            <span style={{ fontWeight: 'bold', color: '#60a5fa' }}>{tooltipData.impliedProb}%</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Edge:</span>
            <span style={{ fontWeight: 'bold', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#f87171' }}>
              {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
            </span>
          </div>
        </div>
      )}

      {/* Mobile bottom sheet with Add/Remove to Betslip — matches NFL. */}
      {tooltipData && isMobile && (
        <div
          role="dialog"
          aria-label="Odds detail"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '14px 16px calc(16px + env(safe-area-inset-bottom))',
            boxShadow: '0 -6px 20px rgba(0,0,0,0.5)',
            borderTop: '1px solid #374151',
            borderTopLeftRadius: '14px',
            borderTopRightRadius: '14px',
            zIndex: 1210,
            fontSize: '0.9rem',
            animation: 'toastFadeUp 0.2s ease-out',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1)} ({tooltipData.point})
            </span>
            <button
              type="button"
              onClick={() => setTooltipData(null)}
              aria-label="Close"
              style={{ background: 'transparent', border: 'none', color: '#d1d5db', fontSize: '1.4rem', lineHeight: 1, padding: '4px 10px', cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Odds</div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Implied</div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#60a5fa' }}>{tooltipData.impliedProb}%</div>
            </div>
            <div>
              <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Edge</div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#f87171' }}>
                {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
              </div>
            </div>
          </div>
          {(() => {
            const m = tooltipData.meta;
            if (!m) return null;
            const stripSportPrefix = (id) => String(id ?? '').replace(/^[a-z]+_/i, '');
            const normLine = (v) => {
              if (v == null || v === '') return v;
              const n = parseFloat(v);
              return Number.isFinite(n) ? n : v;
            };
            const targetPlayerId = stripSportPrefix(m.playerId);
            const targetLine = normLine(m.line);
            const existing = betslipItems.find(bet =>
              bet.gameId === m.gameId &&
              stripSportPrefix(bet.playerId) === targetPlayerId &&
              bet.market === m.marketKey &&
              normLine(bet.line) === targetLine &&
              bet.side === m.side
            );
            const btnBase = { width: '100%', marginTop: '12px', padding: '12px 14px', fontSize: '0.95rem', fontWeight: 700, border: 'none', borderRadius: '10px', cursor: 'pointer', color: '#fff' };
            if (existing) {
              return (
                <button type="button" style={{ ...btnBase, backgroundColor: '#dc2626' }} onClick={() => { if (onRemoveBet) onRemoveBet(existing.id); setTooltipData(null); }}>
                  Remove from Betslip
                </button>
              );
            }
            return (
              <button type="button" style={{ ...btnBase, backgroundColor: '#10b981' }} onClick={() => { if (onOddsClick) onOddsClick(m.gameId, m.playerId, m.playerName, m.marketKey, m.book, m.odds, m.awayTeam, m.homeTeam, m.playerTeam, m.playerPosition, m.line, m.side); setTooltipData(null); }}>
                Add to Betslip
              </button>
            );
          })()}
        </div>
      )}
    </Modal>
  );
};

export default NBAPlayerModal;
